import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, BACKEND_URL, mediaUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Navbar } from "../components/Navbar";
import { UserAvatar, EfsaneBadge } from "../components/UserAvatar";
import { toast } from "sonner";
import { Send, MessageCircle, Check, CheckCheck, ArrowLeft, Image as ImageIcon, Reply, Trash2, X } from "lucide-react";

export default function Messages() {
  const { username } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [other, setOther] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [otherTyping, setOtherTyping] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [uploading, setUploading] = useState(false);
  const endRef = useRef();
  const wsRef = useRef(null);
  const fileRef = useRef();
  const typingSentRef = useRef(false);
  const typingTimeoutRef = useRef(null);

  const loadConvs = async () => {
    try { const { data } = await api.get("/dm/conversations"); setConversations(data); } catch {}
  };
  useEffect(() => { loadConvs(); }, []);

  useEffect(() => {
    setMessages([]); setOther(null); setOtherTyping(false); setReplyTo(null);
    if (!username) return;
    let active = true;
    api.get(`/dm/${username}`).then(({ data }) => { if (!active) return; setOther(data.user); setMessages(data.messages); });

    const token = localStorage.getItem("token");
    const ws = new WebSocket(`${BACKEND_URL.replace(/^http/, "ws")}/api/ws/dm/${username}?token=${token}`);
    wsRef.current = ws;
    ws.onmessage = (ev) => {
      const msg = JSON.parse(ev.data);
      if (msg.type === "dm") {
        setMessages((m) => m.some((x) => x.id === msg.message.id) ? m : [...m, msg.message]);
        if (msg.message.from_id !== user.id) { try { ws.send(JSON.stringify({ type: "seen" })); } catch {} }
        loadConvs();
      } else if (msg.type === "typing") {
        if (msg.by !== user.id) setOtherTyping(msg.typing);
      } else if (msg.type === "seen") {
        if (msg.by !== user.id) setMessages((m) => m.map((x) => x.from_id === user.id ? { ...x, seen: true } : x));
      } else if (msg.type === "delete") {
        setMessages((m) => m.map((x) => x.id === msg.id ? { ...x, deleted: true, text: "", image_url: null } : x));
      }
    };
    return () => { active = false; ws.close(); };
    // eslint-disable-next-line
  }, [username]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, otherTyping]);

  const onChange = (e) => {
    setText(e.target.value);
    const ws = wsRef.current;
    if (!ws) return;
    if (!typingSentRef.current) { try { ws.send(JSON.stringify({ type: "typing", typing: true })); } catch {}; typingSentRef.current = true; }
    clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => { try { ws.send(JSON.stringify({ type: "typing", typing: false })); } catch {}; typingSentRef.current = false; }, 1500);
  };

  const sendPayload = (payload) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== 1) { toast.error("Bağlantı kurulamadı"); return; }
    ws.send(JSON.stringify({ type: "dm", ...payload, reply_to: replyTo ? { id: replyTo.id, text: replyTo.text, image: !!replyTo.image_url, from: replyTo.from_username } : null }));
    setReplyTo(null);
  };

  const send = (e) => {
    e.preventDefault();
    const val = text.trim();
    if (!val) return;
    sendPayload({ text: val });
    try { wsRef.current?.send(JSON.stringify({ type: "typing", typing: false })); } catch {}
    typingSentRef.current = false;
    setText("");
  };

  const onPickImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      sendPayload({ text: "", image_url: data.url });
    } catch { toast.error("Fotoğraf gönderilemedi"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const deleteMsg = (id) => {
    try { wsRef.current?.send(JSON.stringify({ type: "delete", id })); } catch {}
  };

  const lastMineId = [...messages].reverse().find((m) => m.from_id === user.id)?.id;

  return (
    <div className="min-h-screen bg-[#141414]">
      <Navbar />
      <div className="max-w-6xl mx-auto px-2 sm:px-4 py-4 sm:py-6">
        <h1 className="text-2xl sm:text-3xl font-black text-white mb-4 px-2">Mesajlar</h1>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4 h-[75vh]">
          <div className={`bg-[#181818] border border-white/10 rounded-xl overflow-y-auto no-scrollbar ${username ? "hidden md:block" : "block"}`}>
            {conversations.length === 0 && <p className="p-4 text-sm text-gray-500">Henüz mesajın yok. Bir profilden mesaj gönder.</p>}
            {conversations.map((c) => (
              <button key={c.user.id} data-testid={`conv-${c.user.username}`} onClick={() => navigate(`/messages/${c.user.username}`)}
                className={`flex items-center gap-3 w-full px-4 py-3 hover:bg-white/5 text-left border-b border-white/5 ${username === c.user.username ? "bg-white/10" : ""}`}>
                <UserAvatar user={c.user} premium={c.user.is_premium} className="h-11 w-11" />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-white flex items-center gap-1.5">@{c.user.username} {c.user.is_premium && <EfsaneBadge />}</div>
                  <div className="text-xs text-gray-500 truncate">{c.last_message}</div>
                </div>
              </button>
            ))}
          </div>

          <div className={`md:col-span-2 bg-[#181818] border border-white/10 rounded-xl flex flex-col min-h-0 ${username ? "flex" : "hidden md:flex"}`}>
            {other ? (
              <>
                <div className="flex items-center gap-3 px-4 py-3 border-b border-white/10 shrink-0">
                  <button className="md:hidden text-gray-400" onClick={() => navigate("/messages")}><ArrowLeft size={20} /></button>
                  <UserAvatar user={other} premium={other.is_premium} className="h-9 w-9 cursor-pointer" onClick={() => navigate(`/u/${other.username}`)} />
                  <div>
                    <button onClick={() => navigate(`/u/${other.username}`)} className="text-white font-semibold hover:underline flex items-center gap-1.5">@{other.username} {other.is_premium && <EfsaneBadge />}</button>
                    {otherTyping && <span data-testid="dm-typing" className="text-xs text-[#E50914] animate-pulse">yazıyor...</span>}
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto no-scrollbar p-4 space-y-1.5 min-h-0">
                  {messages.map((m) => {
                    const mine = m.from_id === user.id;
                    return (
                      <div key={m.id} className={`group flex flex-col ${mine ? "items-end" : "items-start"}`}>
                        <div className={`flex items-center gap-1.5 ${mine ? "flex-row-reverse" : "flex-row"}`}>
                          <div className={`max-w-[78%] rounded-2xl overflow-hidden text-sm break-words ${m.deleted ? "bg-white/5 text-gray-500 italic px-3.5 py-2" : mine ? "bg-[#E50914] text-white rounded-br-sm" : "bg-[#2B2B2B] text-gray-100 rounded-bl-sm"}`}>
                            {m.deleted ? (
                              "Bu mesaj silindi"
                            ) : (
                              <>
                                {m.reply_to && (
                                  <div className="mx-2 mt-2 mb-1 px-2 py-1 rounded-lg bg-black/25 border-l-2 border-white/40 text-xs opacity-90">
                                    <div className="font-semibold">@{m.reply_to.from}</div>
                                    <div className="truncate">{m.reply_to.image ? "📷 Fotoğraf" : m.reply_to.text}</div>
                                  </div>
                                )}
                                {m.image_url && <img src={mediaUrl(m.image_url)} alt="" className="max-w-full max-h-72 object-cover" />}
                                {m.text && <div className="px-3.5 py-2">{m.text}</div>}
                              </>
                            )}
                          </div>
                          {!m.deleted && (
                            <div className="opacity-0 group-hover:opacity-100 flex gap-1 transition-opacity">
                              <button data-testid={`dm-reply-${m.id}`} onClick={() => setReplyTo(m)} className="text-gray-500 hover:text-white p-1"><Reply size={14} /></button>
                              {mine && <button data-testid={`dm-delete-${m.id}`} onClick={() => deleteMsg(m.id)} className="text-gray-500 hover:text-red-500 p-1"><Trash2 size={14} /></button>}
                            </div>
                          )}
                        </div>
                        {mine && m.id === lastMineId && !m.deleted && (
                          <span data-testid="dm-receipt" className="flex items-center gap-1 text-[11px] text-gray-500 mt-0.5 pr-1">
                            {m.seen ? <><CheckCheck size={13} className="text-[#3ea6ff]" /> Görüldü</> : <><Check size={13} /> İletildi</>}
                          </span>
                        )}
                      </div>
                    );
                  })}
                  <div ref={endRef} />
                </div>

                {replyTo && (
                  <div className="flex items-center gap-2 px-4 py-2 border-t border-white/10 bg-white/5 text-xs shrink-0">
                    <Reply size={14} className="text-[#E50914]" />
                    <div className="flex-1 min-w-0"><span className="text-gray-400">@{replyTo.from_username}:</span> <span className="text-gray-300 truncate">{replyTo.image_url ? "📷 Fotoğraf" : replyTo.text}</span></div>
                    <button onClick={() => setReplyTo(null)} className="text-gray-500 hover:text-white"><X size={14} /></button>
                  </div>
                )}

                <form onSubmit={send} className="flex items-center gap-2 p-3 border-t border-white/10 shrink-0">
                  <button type="button" data-testid="dm-image-button" onClick={() => fileRef.current?.click()} disabled={uploading} className="text-gray-400 hover:text-[#E50914] transition shrink-0">
                    <ImageIcon size={22} />
                  </button>
                  <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPickImage} data-testid="dm-file-input" />
                  <input data-testid="dm-input" value={text} onChange={onChange} placeholder={uploading ? "Fotoğraf yükleniyor..." : "Mesaj yaz..."}
                    className="flex-1 bg-[#2B2B2B] border border-white/10 text-white rounded-full px-4 py-2.5 focus:outline-none focus:border-[#E50914]" />
                  <button data-testid="dm-send" type="submit" className="bg-[#E50914] hover:bg-[#F40612] text-white p-2.5 rounded-full transition shrink-0"><Send size={18} /></button>
                </form>
              </>
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-gray-500">
                <MessageCircle size={48} className="mb-3 text-gray-700" /> Bir sohbet seç
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
